// src/services/api.js
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({ baseURL: API_BASE });

// ── Request interceptor — attach token, set Content-Type ─
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// ── Response interceptor — handle token refresh ───────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);

        original.headers.Authorization = `Bearer ${data.data.accessToken}`;
        return api(original);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    const message = error.response?.data?.message || 'An error occurred';
    if (error.response?.status !== 401) toast.error(message);

    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login:    (data) => api.post('/auth/login', data),
  logout:   (data) => api.post('/auth/logout', data),
  me:       ()     => api.get('/auth/me'),
};

// ── Organisations ─────────────────────────────────────────
export const orgAPI = {
  // Organisations
  create:        (data)          => api.post('/orgs', data),
  getMine:       ()              => api.get('/orgs/mine'),
  getOne:        (orgId)         => api.get(`/orgs/${orgId}`),
  update:        (orgId, data)   => api.patch(`/orgs/${orgId}`, data),
  delete:        (orgId)         => api.delete(`/orgs/${orgId}`),

  // Members
  getMembers:    (orgId)         => api.get(`/orgs/${orgId}/members`),
  invite:        (orgId, data)   => api.post(`/orgs/${orgId}/members/invite`, data),
  acceptInvite:  (memberId)      => api.post(`/orgs/invites/${memberId}/accept`),
  updateRole:    (orgId, memberId, data) => api.patch(`/orgs/${orgId}/members/${memberId}`, data),
  removeMember:  (orgId, memberId)      => api.delete(`/orgs/${orgId}/members/${memberId}`),

  // Group Profiles
  getProfiles:   (orgId)                => api.get(`/orgs/${orgId}/profiles`),
  createProfile: (orgId, data)          => api.post(`/orgs/${orgId}/profiles`, data),
  getProfile:    (orgId, profileId)     => api.get(`/orgs/${orgId}/profiles/${profileId}`),
  updateProfile: (orgId, profileId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}`, data),
  deleteProfile: (orgId, profileId)     => api.delete(`/orgs/${orgId}/profiles/${profileId}`),

  // Profile Members
  getProfileMembers:       (orgId, profileId)               => api.get(`/orgs/${orgId}/profiles/${profileId}/members`),
  inviteProfileMember:     (orgId, profileId, data)         => api.post(`/orgs/${orgId}/profiles/${profileId}/members/invite`, data),
  acceptProfileInvite:     (memberId)                       => api.post(`/orgs/profile-invites/${memberId}/accept`),
  updateProfileMemberRole: (orgId, profileId, memberId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/members/${memberId}`, data),
  removeProfileMember:     (orgId, profileId, memberId)     => api.delete(`/orgs/${orgId}/profiles/${profileId}/members/${memberId}`),

  // Workplaces
  getWorkplaces:    (orgId, profileId)                      => api.get(`/orgs/${orgId}/profiles/${profileId}/workplaces`),
  createWorkplace:  (orgId, profileId, data)                => api.post(`/orgs/${orgId}/profiles/${profileId}/workplaces`, data),
  updateWorkplace:  (orgId, profileId, workplaceId, data)   => api.patch(`/orgs/${orgId}/profiles/${profileId}/workplaces/${workplaceId}`, data),
  deleteWorkplace:  (orgId, profileId, workplaceId)         => api.delete(`/orgs/${orgId}/profiles/${profileId}/workplaces/${workplaceId}`),

  // Job definitions
  getJobDefs:    (orgId, profileId)               => api.get(`/orgs/${orgId}/profiles/${profileId}/job-definitions`),
  createJobDef:  (orgId, profileId, data)         => api.post(`/orgs/${orgId}/profiles/${profileId}/job-definitions`, data),
  parseJobDef:   (orgId, profileId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/orgs/${orgId}/profiles/${profileId}/job-definitions/parse`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  updateJobDef:  (orgId, profileId, jobDefId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/job-definitions/${jobDefId}`, data),
  deleteJobDef:  (orgId, profileId, jobDefId)     => api.delete(`/orgs/${orgId}/profiles/${profileId}/job-definitions/${jobDefId}`),

  // Profile contacts
  getProfileContacts:    (orgId, profileId)                  => api.get(`/orgs/${orgId}/profiles/${profileId}/contacts`),
  createProfileContact:  (orgId, profileId, data)            => api.post(`/orgs/${orgId}/profiles/${profileId}/contacts`, data),
  updateProfileContact:  (orgId, profileId, contactId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/contacts/${contactId}`, data),
  deleteProfileContact:  (orgId, profileId, contactId)       => api.delete(`/orgs/${orgId}/profiles/${profileId}/contacts/${contactId}`),
};

// ── Apply Filters (standalone, owned by profile) ─────────
export const applyFilterAPI = {
  list:   (orgId, profileId)             => api.get(`/orgs/${orgId}/profiles/${profileId}/apply-filters`),
  get:    (orgId, profileId, filterId)   => api.get(`/orgs/${orgId}/profiles/${profileId}/apply-filters/${filterId}`),
  create: (orgId, profileId, data)       => api.post(`/orgs/${orgId}/profiles/${profileId}/apply-filters`, data),
  update: (orgId, profileId, filterId, data) => api.put(`/orgs/${orgId}/profiles/${profileId}/apply-filters/${filterId}`, data),
  delete: (orgId, profileId, filterId)   => api.delete(`/orgs/${orgId}/profiles/${profileId}/apply-filters/${filterId}`),
};

// ── Text Snippets ─────────────────────────────────────────
export const snippetAPI = {
  getSnippets:   (orgId, profileId)                       => api.get(`/orgs/${orgId}/profiles/${profileId}/snippets`),
  createSnippet: (orgId, profileId, data)                 => api.post(`/orgs/${orgId}/profiles/${profileId}/snippets`, data),
  updateSnippet: (orgId, profileId, snippetId, data)      => api.patch(`/orgs/${orgId}/profiles/${profileId}/snippets/${snippetId}`, data),
  deleteSnippet: (orgId, profileId, snippetId)            => api.delete(`/orgs/${orgId}/profiles/${profileId}/snippets/${snippetId}`),
};

// ── Job Seeker ────────────────────────────────────────────
export const seekerAPI = {
  getProfile:         ()     => api.get('/seekers/profile'),
  updateProfile:      (data) => api.patch('/seekers/profile', data),
  uploadProfilePhoto: (form) => api.post('/seekers/profile/photo', form),
  getCVs:             ()     => api.get('/seekers/cv'),
  uploadCV:           (form) => api.post('/seekers/cv/upload', form),
  deleteCV:           (id)   => api.delete(`/seekers/cv/${id}`),
  setPrimaryCV:       (id)   => api.patch(`/seekers/cv/${id}/primary`),
  getCVById:          (id)   => api.get(`/seekers/cv/${id}`),
  markCVReviewed:     (id)   => api.patch(`/seekers/cv/${id}/reviewed`),
  searchSkills:       (q)    => api.get('/seekers/skills/all', { params: { q } }),
  extractSkills:      (data) => api.post('/seekers/skills/extract', data),
  clearSourceLinks:   (data) => api.post('/seekers/skills/clear-source-links', data),
  addSkill:           (data) => api.post('/seekers/skills', data),
  removeSkill:        (skillId) => api.delete(`/seekers/skills/${skillId}`),
  updateSkillType:    (skillId, skillType) => api.patch(`/seekers/skills/${skillId}/type`, { skillType }),
  getSkillDerivations: () => api.get('/seekers/skills/derivations'),
  addEducationLink:   (data) => api.post('/seekers/skills/education-links', data),
  deleteEducationLink: (linkId) => api.delete(`/seekers/skills/education-links/${linkId}`),
  addExperienceLink:  (data) => api.post('/seekers/skills/experience-links', data),
  deleteExperienceLink: (linkId) => api.delete(`/seekers/skills/experience-links/${linkId}`),
  createCustomSection: (data) => api.post('/seekers/skills/sections', data),
  updateCustomSection: (sectionId, data) => api.patch(`/seekers/skills/sections/${sectionId}`, data),
  deleteCustomSection: (sectionId) => api.delete(`/seekers/skills/sections/${sectionId}`),
  addSkillToCustomSection: (sectionId, data) => api.post(`/seekers/skills/sections/${sectionId}/skills`, data),
  removeSkillFromCustomSection: (sectionId, jobSeekerSkillId) => api.delete(`/seekers/skills/sections/${sectionId}/skills/${jobSeekerSkillId}`),
  getContacts:        ()     => api.get('/seekers/contacts'),
  addContact:         (data) => api.post('/seekers/contacts', data),
  updateContact:      (id, data) => api.patch(`/seekers/contacts/${id}`, data),
  deleteContact:      (id)   => api.delete(`/seekers/contacts/${id}`),
  getCVProfiles:      ()     => api.get('/seekers/cv-profiles'),
  getCVProfileById:   (id)   => api.get(`/seekers/cv-profiles/${id}`),
  createCVProfile:    (data) => api.post('/seekers/cv-profiles', data),
  updateCVProfile:    (id, data) => api.patch(`/seekers/cv-profiles/${id}`, data),
  deleteCVProfile:    (id)   => api.delete(`/seekers/cv-profiles/${id}`),
  getLanguages:       ()     => api.get('/seekers/languages'),
  addLanguage:        (data) => api.post('/seekers/languages', data),
  removeLanguage:     (id)   => api.delete(`/seekers/languages/${id}`),
  getEducation:       ()     => api.get('/seekers/education'),
  addEducation:       (data) => api.post('/seekers/education', data),
  updateEducation:    (id, data) => api.patch(`/seekers/education/${id}`, data),
  deleteEducation:    (id)   => api.delete(`/seekers/education/${id}`),
  getExperience:      ()     => api.get('/seekers/experience'),
  addExperience:      (data) => api.post('/seekers/experience', data),
  updateExperience:   (id, data) => api.patch(`/seekers/experience/${id}`, data),
  deleteExperience:   (id)   => api.delete(`/seekers/experience/${id}`),
  getReferences:      ()     => api.get('/seekers/references'),
  addReference:       (data) => api.post('/seekers/references', data),
  updateReference:    (id, data) => api.patch(`/seekers/references/${id}`, data),
  deleteReference:    (id)   => api.delete(`/seekers/references/${id}`),
  applyToPosition:    (positionId, data) => api.post(`/seekers/apply/${positionId}`, data || {}),
  autocompletePositionNames: (q) => api.get('/seekers/autocomplete/position-names', { params: { q } }),
  autocompleteEmployerNames: (q) => api.get('/seekers/autocomplete/employer-names', { params: { q } }),
};

// ── Job Ads (public) ──────────────────────────────────────
export const jobAdPublicAPI = {
  getActive:  (params) => api.get('/job-ads', { params }),
  getById:    (id)     => api.get(`/job-ads/${id}`),
};

// ── Group Profile Media (employer, profile-scoped) ────────
export const mediaAPI = {
  list:   (orgId, profileId, type)       => api.get(`/orgs/${orgId}/profiles/${profileId}/media`, { params: type ? { type } : undefined }),
  upload: (orgId, profileId, file, type, name) => {
    const fd = new FormData();
    fd.append('file', file);
    if (type) fd.append('type', type);
    if (name) fd.append('name', name);
    return api.post(`/orgs/${orgId}/profiles/${profileId}/media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  rename: (orgId, profileId, mediaId, name) =>
    api.patch(`/orgs/${orgId}/profiles/${profileId}/media/${mediaId}`, { name }),
  remove: (orgId, profileId, mediaId) =>
    api.delete(`/orgs/${orgId}/profiles/${profileId}/media/${mediaId}`),
};

// ── Job Ads (employer, profile-scoped) ────────────────────
export const jobAdAPI = {
  getJobAds:      (orgId, profileId)                       => api.get(`/orgs/${orgId}/profiles/${profileId}/job-ads`),
  getJobAd:       (orgId, profileId, jobAdId)             => api.get(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}`),
  createJobAd:    (orgId, profileId, data)        => api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads`, data),
  parseJobAd:     (orgId, profileId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/parse`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  updateJobAd:    (orgId, profileId, jobAdId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}`, data),
  deleteJobAd:    (orgId, profileId, jobAdId)     => api.delete(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}`),
  publishJobAd:   (orgId, profileId, jobAdId)     => api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/publish`),
  closeJobAd:     (orgId, profileId, jobAdId)     => api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/close`),
  createPosition: (orgId, profileId, jobAdId, data) => api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/positions`, data),
  parsePosition:  (orgId, profileId, jobAdId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/positions/parse`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  updatePosition: (orgId, profileId, jobAdId, positionId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/positions/${positionId}`, data),
  deletePosition: (orgId, profileId, jobAdId, positionId) => api.delete(`/orgs/${orgId}/profiles/${profileId}/job-ads/${jobAdId}/positions/${positionId}`),
  // Matching
  runMatch:          (orgId, profileId, adId)             => api.post(`/orgs/${orgId}/profiles/${profileId}/job-ads/${adId}/run-match`),
  getMatches:        (orgId, profileId, adId, params)     => api.get(`/orgs/${orgId}/profiles/${profileId}/job-ads/${adId}/matches`, { params }),
  updateMatchStatus: (orgId, profileId, adId, matchId, data) => api.patch(`/orgs/${orgId}/profiles/${profileId}/job-ads/${adId}/matches/${matchId}`, data),
  // Candidates
  getCandidates:        (orgId, profileId)                    => api.get(`/orgs/${orgId}/profiles/${profileId}/candidates`),
  runPositionMatching:  (orgId, profileId, positionId, data)  => api.post(`/orgs/${orgId}/profiles/${profileId}/candidates/${positionId}/run`, data),
  getCandidateDetail:   (orgId, profileId, matchId)           => api.get(`/orgs/${orgId}/profiles/${profileId}/candidates/${matchId}`),
  getMatchingModels:    (orgId, profileId)                    => api.get(`/orgs/${orgId}/profiles/${profileId}/matching-models`),
};

// ── Admin ─────────────────────────────────────────────────
export const adminAPI = {
  getUsers:     (params) => api.get('/admin/users', { params }),
  updateUser:   (id, d)  => api.patch(`/admin/users/${id}`, d),
};

// ── Matching Engine Model Builder (admin) ────────────────
export const matchingModelAPI = {
  // Circumstances scorer config
  getCircumstancesConfig:    ()       => api.get('/admin/matching/circumstances-config'),
  updateCircumstancesConfig: (data)   => api.patch('/admin/matching/circumstances-config', data),
  // Seeker preferences scorer config
  getPreferencesConfig:      ()       => api.get('/admin/matching/preferences-config'),
  updatePreferencesConfig:   (data)   => api.patch('/admin/matching/preferences-config', data),
  // Abilities scorer config
  getAbilitiesConfig:        ()       => api.get('/admin/matching/abilities-config'),
  updateAbilitiesConfig:     (data)   => api.patch('/admin/matching/abilities-config', data),
  // Raw skills scorer config
  getRawSkillsConfig:        ()       => api.get('/admin/matching/raw-skills-config'),
  updateRawSkillsConfig:     (data)   => api.patch('/admin/matching/raw-skills-config', data),
  // Education scorer config
  getEducationConfig:        ()       => api.get('/admin/matching/education-config'),
  updateEducationConfig:     (data)   => api.patch('/admin/matching/education-config', data),
  // Experience scorer config
  getExperienceConfig:       ()       => api.get('/admin/matching/experience-config'),
  updateExperienceConfig:    (data)   => api.patch('/admin/matching/experience-config', data),
  // Language scorer config
  getLanguageConfig:         ()       => api.get('/admin/matching/language-config'),
  updateLanguageConfig:      (data)   => api.patch('/admin/matching/language-config', data),
  // Attributes scorer config
  getAttributesConfig:       ()       => api.get('/admin/matching/attributes-config'),
  updateAttributesConfig:    (data)   => api.patch('/admin/matching/attributes-config', data),
  // Models CRUD
  listModels:                ()       => api.get('/admin/matching/models'),
  createModel:               (data)   => api.post('/admin/matching/models', data),
  getModel:                  (id)     => api.get(`/admin/matching/models/${id}`),
  updateModel:               (id, d)  => api.patch(`/admin/matching/models/${id}`, d),
  deleteModel:               (id)     => api.delete(`/admin/matching/models/${id}`),
  duplicateModel:            (id)     => api.post(`/admin/matching/models/${id}/duplicate`),
};

export default api;
